import * as React from 'react'
import { cn } from '@bnsn/ui/lib/utils'

export interface SelectableCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Whether the card is currently selected
   */
  isSelected?: boolean
  /**
   * Callback when the card is clicked
   */
  onSelect?: () => void
  /**
   * Whether the card is disabled
   */
  disabled?: boolean
}

/**
 * A versatile card component that can be selected/clicked with keyboard and mouse support.
 *
 * Features:
 * - Visual selection state with border and shadow changes
 * - Keyboard accessibility (Enter/Space key support)
 * - Hover effects with smooth animations
 * - Disabled state support
 * - Focus management for screen readers
 *
 * @example
 * ```tsx
 * <SelectableCard
 *   isSelected={selectedId === 'card1'}
 *   onSelect={() => setSelectedId('card1')}
 * >
 *   Card content goes here
 * </SelectableCard>
 * ```
 */
const SelectableCard = React.forwardRef<HTMLDivElement, SelectableCardProps>(
  ({ className, isSelected = false, onSelect, disabled = false, children, ...props }, ref) => {
    /**
     * Handles mouse click events
     * Only triggers if not disabled and onSelect callback is provided
     */
    const handleClick = () => {
      if (!disabled && onSelect) {
        onSelect()
      }
    }

    /**
     * Handles keyboard events for accessibility
     * Triggers selection on Enter or Space key press
     */
    const handleKeyDown = (event: React.KeyboardEvent) => {
      if ((event.key === 'Enter' || event.key === ' ') && !disabled && onSelect) {
        event.preventDefault() // Prevent default scrolling behavior for Space key
        onSelect()
      }
    }

    return (
      <div
        ref={ref}
        className={cn(
          // Base card styling with rounded corners and background
          'rounded-xl border-2 bg-white text-card-foreground transition-all duration-300 ease-in-out',
          // Subtle shadow for depth - softer than typical shadows
          'shadow-[0_10px_30px_rgba(0,0,0,0.08),0_4px_10px_rgba(0,0,0,0.05)]',
          // Make cursor pointer only when interactive and not disabled
          onSelect && !disabled && 'cursor-pointer',
          // Enhanced hover effects with border color change, stronger shadow, and slight lift
          onSelect &&
            !disabled &&
            'hover:border-primary hover:shadow-[0_15px_40px_rgba(59,130,246,0.2),0_8px_20px_rgba(0,0,0,0.1)] hover:-translate-y-0.5',
          // Focus ring for keyboard navigation accessibility
          onSelect && !disabled && 'focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
          // Dynamic border color based on selection state
          isSelected ? 'border-primary' : 'border-slate-200',
          // Disabled state reduces opacity and removes pointer cursor
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        // Make focusable only if interactive and not disabled
        tabIndex={onSelect && !disabled ? 0 : undefined}
        // ARIA role for screen readers when interactive
        role={onSelect ? 'button' : undefined}
        // ARIA state for selection when interactive
        aria-pressed={onSelect ? isSelected : undefined}
        // ARIA state for disabled
        aria-disabled={disabled}
        {...props}
      >
        {children}
      </div>
    )
  },
)
SelectableCard.displayName = 'SelectableCard'

export { SelectableCard }
